"use strict";
/**
 * §4.4's gate check — nothing in this system points a foreign key into a schema it does not own
 * (`RPM-081` acceptance row 3, N15, `CR-31`, O3).
 *
 * `docs/rpm/01-RPM-SPEC.md` §4.4 states the rule and the check in one paragraph: *"the reference is
 * stored as the external key's own type (`varchar(20)` for a facility); there is no `REFERENCES`
 * clause; the application validates on write and tolerates a dangling reference on read … **Gate
 * check.** A CI query fails on any `REFERENCES carease.` in an applied migration."*
 *
 * The subject is a schema change that adds a foreign key into the mirror. A constraint into
 * `carease` breaks on the next load of a system ChartVN does not control: the loader drops and
 * recreates `carease.<table>` on every run (`.claude/rules/external-data.md`), and a dependent
 * foreign key makes that drop fail — so the mirror stops refreshing, or the constraint is dropped
 * quietly and the guarantee it claimed is gone.
 *
 * Two populations, because a reference can arrive two ways and each blinds a check over the other:
 *
 *   - the committed migrations, which is where a `REFERENCES carease.` written by hand or generated
 *     by drizzle-kit would appear;
 *   - the live catalogue, which is where one added by any other route — a manual `ALTER TABLE`, a
 *     `drizzle-kit push`, a restored dump — would appear, and where a migration that was edited
 *     after it was applied would not.
 *
 * Both entry points REFUSE an empty population rather than reporting a clean run over nothing. A
 * check whose glob matches no files and passes is the failure this repository has shipped before.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.MIRRORED_SCHEMAS = void 0;
exports.findExternalSchemaReferences = findExternalSchemaReferences;
exports.findForeignKeysIntoMirror = findForeignKeysIntoMirror;
/** The schemas this system mirrors read-only and must never constrain itself against. */
exports.MIRRORED_SCHEMAS = ["carease"];
/** Line comments and block comments: a schema name inside a comment constrains nothing. */
function withoutComments(sql) {
    return sql.replace(/\/\*[\s\S]*?\*\//g, " ").replace(/--[^\n]*/g, " ");
}
const ANY_REFERENCE = /\breferences\s+(?:"([^"]+)"|([a-z_][a-z0-9_$]*))\s*\.\s*(?:"([^"]+)"|([a-z_][a-z0-9_$]*))/gi;
/**
 * Every `REFERENCES <schema>.<table>` in the given migrations whose schema is a mirrored one.
 *
 * @throws when no migration was given. The check's whole value is that it runs over the real set.
 */
function findExternalSchemaReferences(files) {
    if (files.length === 0)
        throw new Error("REFERENCES_EXTERNAL_SCHEMA: no migration was scanned. A check over an empty set reports on nothing, so it fails instead of passing.");
    const mirrored = new Set(exports.MIRRORED_SCHEMAS);
    const findings = [];
    let references = 0;
    for (const { file, sql } of files) {
        for (const m of withoutComments(sql).matchAll(ANY_REFERENCE)) {
            references += 1;
            const schema = (m[1] ?? m[2] ?? "").toLowerCase();
            const table = m[3] ?? m[4] ?? "";
            if (!mirrored.has(schema))
                continue;
            findings.push({
                code: "REFERENCES_EXTERNAL_SCHEMA",
                where: file,
                detail: `declares REFERENCES ${schema}.${table}; ${schema} is a read-only mirror this system does not write, so a foreign key into it breaks on the next load (§4.4, CR-31)`,
            });
        }
    }
    return { findings, inspected: files.length, references };
}
/**
 * Every deployed foreign key whose target lives in a mirrored schema.
 *
 * @throws when the catalogue query returned nothing, which means the query is wrong rather than
 * that the database has no foreign keys.
 */
function findForeignKeysIntoMirror(rows) {
    if (rows.length === 0)
        throw new Error("REFERENCES_EXTERNAL_SCHEMA: the catalogue returned no foreign key at all. This schema declares dozens, so an empty result is a broken query and not a clean database.");
    const mirrored = new Set(exports.MIRRORED_SCHEMAS);
    const findings = rows
        .filter((r) => mirrored.has(r.referencedSchema.toLowerCase()))
        .map((r) => ({
        code: "REFERENCES_EXTERNAL_SCHEMA",
        where: r.constraintName,
        detail: `${r.table} references ${r.referencedSchema}.${r.referencedTable}; ${r.referencedSchema} is a read-only mirror this system does not write (§4.4, CR-31)`,
    }));
    return { findings, inspected: rows.length };
}
//# sourceMappingURL=external-references.js.map